import { Box } from "@mui/material";
import { useRef, useEffect, useState } from "react";
import PropTypes from "prop-types";

import PageView from "./PageView";
import PdfToolbar from "./PdfToolbar";

export default function PdfViewer({ pages, currentPage, setCurrentPage, hoveredChunkId, setHoveredChunkId }) {
    const containerRef = useRef(null);
    const pageRefs = useRef([]);

    const [zoom, setZoom] = useState(1);
    const [pageInput, setPageInput] = useState("");

    /* -----------------------------
       SCROLL → CURRENT PAGE TRACKING
    ------------------------------*/
    useEffect(() => {
        if (!containerRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const page = Number(entry.target.dataset.page);
                        setCurrentPage(page);
                    }
                });
            },
            {
                root: containerRef.current,
                threshold: 0.2, // 20% visible = active page
            }
        );

        pageRefs.current.forEach((el) => {
            if (el) observer.observe(el);
        });

        return () => observer.disconnect();
    }, [pages, setCurrentPage]);

    useEffect(() => {
        setPageInput(String(currentPage));
    }, [currentPage]);

    /* -----------------------------
       NAVIGATION → SCROLL TO PAGE
    ------------------------------*/
    const scrollToPage = (pageNumber) => {
        const element = pageRefs.current[pageNumber - 1];

        if (!element || !containerRef.current) return;

        element.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });

        setCurrentPage(pageNumber);
    };

    return (
        <Box
            sx={{
                height: "100%",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                background: "#525659",
            }}
        >
            {/* TOOLBAR */}
            <PdfToolbar
                pagesCount={pages?.length}
                zoom={zoom}
                pageInput={pageInput}
                setPageInput={setPageInput}
                onGoToPage={() => scrollToPage(Number(pageInput))}
                onZoomIn={() =>
                    setZoom((prev) => Math.min(prev + 0.1, 5))
                }
                onZoomOut={() =>
                    setZoom((prev) => Math.max(prev - 0.1, 0.1))
                }
            />

            {/* VIEWER */}
            <Box
                ref={containerRef}
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "auto",
                    p: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 4,
                    }}
                >
                    {pages?.map((page, index) => (
                        <PageView
                            key={page.id}
                            ref={(el) => (pageRefs.current[index] = el)}
                            page={page}
                            zoom={zoom}
                            currentPage={currentPage}
                            setHoveredChunkId={setHoveredChunkId}
                            hoveredChunkId={hoveredChunkId}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}

PdfViewer.propTypes = {
    pages: PropTypes.array,
    setCurrentPage: PropTypes.func.isRequired,
    hoveredChunkId: PropTypes.string.isRequired,
    setHoveredChunkId: PropTypes.func.isRequired,
};